import { useState } from "react";
import {
    MessageCircle,
    ArrowRight,
    Send,
    User,
    Building2,
    Phone,
    Mail,
    FileText,
    CheckCircle2,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";

export const CtaForm = () => {
    const [form, setForm] = useState({
        nome: "",
        empresa: "",
        telefone: "",
        email: "",
        mensagem: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nome || !form.telefone || !form.email) {
            toast.error("Preencha nome, telefone e e-mail para continuar.");
            return;
        }
        setSubmitting(true);
        // Placeholder: backend futuro irá persistir o lead.
        await new Promise((r) => setTimeout(r, 700));
        setSubmitting(false);
        setSent(true);
        toast.success("Solicitação enviada. Em breve entraremos em contato.");
        setForm({
            nome: "",
            empresa: "",
            telefone: "",
            email: "",
            mensagem: "",
        });
    };

    return (
        <section
            id="contato"
            data-testid="cta-form-section"
            className="relative bg-buzas-navy section-pad overflow-hidden"
        >
            <Toaster position="top-right" richColors />
            {/* decorative */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage:
                        "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                    backgroundSize: "64px 64px",
                    maskImage:
                        "radial-gradient(ellipse at center, black 35%, transparent 80%)",
                }}
            />
            <div
                aria-hidden
                className="absolute -top-32 -left-24 h-80 w-80 rounded-full bg-buzas-red/20 blur-3xl"
            />

            <div className="container-buzas relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                <div className="text-white">
                    <span className="eyebrow text-white/80">
                        Fale com a Buzas
                    </span>
                    <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                        Solicite seu orçamento técnico agora.
                    </h2>
                    <p className="mt-5 text-base md:text-lg text-white/80 leading-relaxed max-w-lg">
                        Fale com um especialista da Buzas.
                    </p>
                    <p className="mt-3 text-sm md:text-base text-white/65 max-w-lg">
                        Envie seu projeto e receba retorno técnico rápido.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <a
                            href="#form-orcamento"
                            data-testid="cta-section-orcamento"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-buzas-red px-6 py-3.5 text-sm font-bold text-white hover:bg-buzas-red-600 transition-colors"
                        >
                            Solicitar orçamento
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                        <a
                            href="https://api.whatsapp.com/send?phone=5511940272123&text=Ol%C3%A1%2C%20quero%20falar%20com%20a%20Buzas"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="cta-section-whatsapp"
                            className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/25 bg-transparent px-6 py-3.5 text-sm font-bold text-white hover:bg-white hover:text-buzas-navy transition-colors"
                        >
                            <MessageCircle className="h-4 w-4" />
                            Falar no WhatsApp
                        </a>
                    </div>

                    <ul className="mt-10 space-y-3 max-w-md">
                        {[
                            "Retorno técnico rápido",
                            "Atendimento a cooperativas e indústria",
                            "Projetos sob medida com suporte",
                        ].map((t) => (
                            <li
                                key={t}
                                className="flex items-start gap-3 text-sm text-white/85"
                            >
                                <CheckCircle2 className="h-5 w-5 text-buzas-red mt-0.5 shrink-0" />
                                <span className="font-semibold">{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <form
                    id="form-orcamento"
                    data-testid="orcamento-form"
                    onSubmit={handleSubmit}
                    className="relative bg-white rounded-sm p-6 md:p-8 shadow-2xl"
                >
                    <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-buzas-red">
                        <FileText className="h-3.5 w-3.5" />
                        Orçamento técnico
                    </div>
                    <h3 className="mt-2 text-xl md:text-2xl font-bold text-buzas-navy">
                        Envie os dados do seu projeto
                    </h3>

                    <div className="mt-6 grid gap-4">
                        <Field
                            icon={User}
                            label="Nome"
                            name="nome"
                            placeholder="Seu nome completo"
                            value={form.nome}
                            onChange={handleChange}
                            testid="input-nome"
                            required
                        />
                        <Field
                            icon={Building2}
                            label="Empresa"
                            name="empresa"
                            placeholder="Nome da empresa"
                            value={form.empresa}
                            onChange={handleChange}
                            testid="input-empresa"
                        />
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                icon={Phone}
                                label="Telefone / WhatsApp"
                                name="telefone"
                                placeholder="(00) 00000-0000"
                                value={form.telefone}
                                onChange={handleChange}
                                testid="input-telefone"
                                required
                            />
                            <Field
                                icon={Mail}
                                label="E‑mail"
                                name="email"
                                type="email"
                                placeholder="voce@empresa.com.br"
                                value={form.email}
                                onChange={handleChange}
                                testid="input-email"
                                required
                            />
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-buzas-navy/70">
                                Mensagem
                            </label>
                            <textarea
                                name="mensagem"
                                data-testid="input-mensagem"
                                rows={4}
                                placeholder="Conte brevemente sobre seu projeto ou peça"
                                value={form.mensagem}
                                onChange={handleChange}
                                className="mt-1.5 w-full rounded-sm border border-buzas-gray-200 bg-white px-4 py-3 text-sm text-buzas-navy placeholder:text-buzas-gray-500 focus:border-buzas-navy focus:outline-none focus:ring-2 focus:ring-buzas-navy/15 resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="form-submit"
                            className="mt-2 inline-flex items-center justify-center gap-2 rounded-sm bg-buzas-red px-6 py-3.5 text-sm font-bold text-white hover:bg-buzas-red-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                            <Send className="h-4 w-4" />
                            {submitting
                                ? "Enviando..."
                                : sent
                                  ? "Enviar novamente"
                                  : "Solicitar orçamento"}
                        </button>

                        <p className="text-[11px] text-buzas-gray-500 leading-relaxed">
                            Ao enviar, você concorda em ser contatado pela
                            equipe técnica da Buzas e com a nossa{" "}
                            <Link
                                to="/politica-de-privacidade"
                                data-testid="form-privacy-policy"
                                className="font-bold underline underline-offset-2 hover:text-buzas-navy"
                            >
                                Política de Privacidade
                            </Link>
                            .
                        </p>
                    </div>
                </form>
            </div>
        </section>
    );
};

const Field = ({
    icon: Icon,
    label,
    name,
    type = "text",
    placeholder,
    value,
    onChange,
    testid,
    required = false,
}) => (
    <div>
        <label
            htmlFor={name}
            className="text-xs font-bold uppercase tracking-wider text-buzas-navy/70"
        >
            {label} {required && <span className="text-buzas-red">*</span>}
        </label>
        <div className="mt-1.5 relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-buzas-gray-500">
                <Icon className="h-4 w-4" />
            </span>
            <input
                id={name}
                name={name}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={testid}
                className="w-full rounded-sm border border-buzas-gray-200 bg-white pl-10 pr-4 py-3 text-sm text-buzas-navy placeholder:text-buzas-gray-500 focus:border-buzas-navy focus:outline-none focus:ring-2 focus:ring-buzas-navy/15"
            />
        </div>
    </div>
);
